// src/components/CampaignManagementTable.jsx
import React, { useMemo, useState } from "react"
import AddIcon from "@mui/icons-material/Add"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  CardTitle,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Spinner,
} from "reactstrap"
import { useSelector } from "react-redux"
import PrimaryButton from "newcomponents/primary-button"

const BLUE = "#2990DF"

const CampaignManagementTable = ({
  campaigns = [],
  setErr,
  setSuccess,
  loading,
}) => {
  const navigate = useNavigate()
  const mode = useSelector(s => s.Layout?.layoutMode)
  const userData = useSelector(state => state.Login.userData)

  const [currentPage, setCurrentPage] = useState(1)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    audience: "", // audience NAME query
  })

  const campaignsPerPage = 7
  const isDark = mode === "dark"
  const titleColor = isDark ? "white" : "#1F2937"

  // Inline styles (no <style> block)
  const sx = {
    filterMenu: {
      minWidth: 240,
      borderRadius: 8,
      border: `1px solid ${isDark ? "#374151" : "#E5E7EB"}`,
      background: isDark ? "#1F2937" : "#FFFFFF",
      boxShadow: isDark
        ? "0 2px 8px rgba(0,0,0,.5)"
        : "0 2px 8px rgba(0,0,0,.1)",
      padding: "6px 0",
    },
    filterItem: {
      fontSize: ".9rem",
      color: isDark ? "#E5E7EB" : "#111827",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    filterInput: {
      width: "100%",
      border: `1px solid ${isDark ? "#374151" : "#E5E7EB"}`,
      borderRadius: 6,
      background: isDark ? "#111827" : "#F9FAFB",
      color: isDark ? "#E5E7EB" : "#111827",
      padding: "6px 8px",
    },
    filterToggleBtn: {
      padding: "8px 12px",
      borderRadius: 8,
      border: "none",
      backgroundColor: "#F3F4F6",
      color: "#6B6B6C",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    },
    divider: { height: ".1px", width: "100%", background: "gray" },
    th: {
      borderBottom: `1px solid ${isDark ? "#374151" : "#EEF2F7"}`,
      background: isDark ? "#111827" : "#FAFBFC",
      color: isDark ? "#9CA3AF" : "#6B7280",
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: ".02em",
    },
    td: {
      verticalAlign: "middle",
      borderTop: `1px solid ${isDark ? "#273244" : "#F1F5F9"}`,
      fontSize: 14,
      color: isDark ? "#F3F4F6" : "#0F172A",
      transition: "background 0.15s ease",
    },
    rowHoverBg: isDark ? "#0B1220" : "#F8FAFC",
    paginationActiveLink: {
      backgroundColor: BLUE,
      borderColor: BLUE,
      color: "#fff",
    },
  }

  // Audience name extraction
  const getAudienceName = dist =>
    (
      dist?.audienceName ??
      dist?.audience?.name ??
      dist?.Audience?.name ??
      ""
    ).toString()

  const applyFilters = c => {
    const { startDate, endDate, audience } = filters

    if (startDate && new Date(c.startDate) < new Date(startDate)) return false
    if (endDate && new Date(c.endDate) > new Date(endDate)) return false

    if (audience) {
      const q = audience.trim().toLowerCase()
      const names =
        c.SurveyDistributions?.map(d => getAudienceName(d).toLowerCase()) ?? []
      if (!names.some(n => n.includes(q))) return false
    }
    return true
  }

  const filtered = useMemo(
    () => (Array.isArray(campaigns) ? campaigns : []).filter(applyFilters),
    [campaigns, filters],
  )

  const totalPages = Math.max(1, Math.ceil(filtered.length / campaignsPerPage))
  const indexOfFirst = (currentPage - 1) * campaignsPerPage
  const pageRows = filtered.slice(indexOfFirst, indexOfFirst + campaignsPerPage)

  const clearFilters = () =>
    setFilters({ startDate: "", endDate: "", audience: "" })

  return (
    <>
      <Card
        className="mb-2 br"
        style={{ height: "100%", backgroundColor: isDark ? "#252F40" : "#fff" }}
      >
        <CardBody
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          {/* Header */}
          <Row className="align-items-center mb-3">
            <Col xl={6} lg={6} md={6} className="d-flex align-items-center">
              <CardTitle
                className="h2 mb-0 text-uppercase"
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: titleColor,
                }}
              >
                Campaigns
              </CardTitle>
            </Col>

            <Col
              xl={6}
              lg={6}
              md={6}
              className="d-flex justify-content-end align-items-center"
            >
              <div style={{ marginRight: 15 }}>
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="button"
                    style={sx.filterToggleBtn}
                    caret={false}
                  >
                    <FilterAltOutlinedIcon />
                  </DropdownToggle>

                  <DropdownMenu end style={sx.filterMenu}>
                    <DropdownItem header className="text-muted">
                      Filter by Date
                    </DropdownItem>

                    <DropdownItem toggle={false} style={sx.filterItem}>
                      <Input
                        type="date"
                        value={filters.startDate}
                        onChange={e =>
                          setFilters({ ...filters, startDate: e.target.value })
                        }
                        style={sx.filterInput}
                      />
                    </DropdownItem>

                    <DropdownItem toggle={false} style={sx.filterItem}>
                      <Input
                        type="date"
                        value={filters.endDate}
                        onChange={e =>
                          setFilters({ ...filters, endDate: e.target.value })
                        }
                        style={sx.filterInput}
                      />
                    </DropdownItem>

                    <DropdownItem header className="text-muted">
                      Filter by Audience
                    </DropdownItem>

                    <DropdownItem toggle={false} style={sx.filterItem}>
                      <Input
                        type="text"
                        placeholder="Audience name"
                        value={filters.audience}
                        onChange={e =>
                          setFilters({ ...filters, audience: e.target.value })
                        }
                        style={sx.filterInput}
                      />
                    </DropdownItem>

                    <DropdownItem divider />

                    {/* Normal item closes the menu */}
                    <DropdownItem onClick={clearFilters} style={sx.filterItem}>
                      Clear Filters
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>

              {userData.role !== "viewer" && (
                <PrimaryButton
                  style={{
                    padding: "10px 20px",
                    fontSize: "16px",
                    fontWeight: 400,
                    borderRadius: 8,
                  }}
                  icon={<AddIcon />}
                  onClick={() => navigate("/dashboard/create-campaign")}
                >
                  Create Campaign
                </PrimaryButton>
              )}
            </Col>
          </Row>

          <div style={sx.divider} />

          {/* Table */}
          <div className="table-responsive" style={{ flex: 1 }}>
            <Table hover style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th style={{ ...sx.th, width: 56 }}>#</th>
                  <th style={sx.th}>Title</th>
                  <th style={sx.th}>Surveys</th>
                  <th style={sx.th}>Audiences</th>
                  <th style={sx.th}>Sentiment</th>
                  <th style={sx.th}>Start Date</th>
                  <th style={sx.th}>End Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4" style={sx.td}>
                      <Spinner
                        color="primary"
                        style={{ height: "3rem", width: "3rem" }}
                      />
                    </td>
                  </tr>
                ) : pageRows.length ? (
                  pageRows.map((campaign, idx) => {
                    const sd = campaign.SurveyDistributions ?? []
                    const uniqueSurveyIds = new Set(sd.map(d => d.surveyId))
                      .size
                    const uniqueAudienceIds = new Set(sd.map(d => d.audienceId))
                      .size
                    const rowKey = campaign.id

                    return (
                      <tr
                        key={rowKey}
                        onMouseEnter={() => setHoveredRow(rowKey)}
                        onMouseLeave={() => setHoveredRow(null)}
                        onClick={() =>
                          navigate(`/dashboard/campaign-details/${campaign.id}`)
                        }
                        style={{
                          cursor: "pointer",
                          background:
                            hoveredRow === rowKey ? sx.rowHoverBg : undefined,
                        }}
                      >
                        <td style={sx.td}>{indexOfFirst + idx + 1}</td>
                        <td style={{ ...sx.td, fontWeight: 600 }}>
                          {campaign.campaignTitle}
                        </td>
                        <td style={sx.td}>{uniqueSurveyIds}</td>
                        <td style={sx.td}>{uniqueAudienceIds}</td>
                        <td style={sx.td}>
                          {campaign.campaignSentimentScore} / 10
                        </td>
                        <td style={sx.td}>
                          {new Date(campaign.startDate).toLocaleDateString()}
                        </td>
                        <td style={sx.td}>
                          {new Date(campaign.endDate).toLocaleDateString()}
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center text-muted py-4"
                      style={sx.td}
                    >
                      No campaigns found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* ✅ Unified Blue-Themed Pagination */}
          <Pagination
            className="mt-0 justify-content-center"
            style={{ marginTop: "auto" }}
          >
            {/* Previous Button */}
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink
                style={{
                  color: isDark ? "#fff" : "#000",
                  backgroundColor: "transparent",
                  borderColor: isDark ? "#444" : "#dee2e6",
                }}
                previous
                onClick={() => setCurrentPage(currentPage - 1)}
              />
            </PaginationItem>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }).map((_, i) => {
              const active = i + 1 === currentPage
              return (
                <PaginationItem active={active} key={i}>
                  <PaginationLink
                    style={{
                      borderColor: active ? BLUE : isDark ? "#444" : "#dee2e6",
                      boxShadow: "none",
                      backgroundColor: active ? BLUE : "transparent",
                      color: active ? "#fff" : isDark ? "#fff" : "#000",
                    }}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              )
            })}

            {/* Next Button */}
            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink
                style={{
                  color: isDark ? "#fff" : "#000",
                  backgroundColor: "transparent",
                  borderColor: isDark ? "#444" : "#dee2e6",
                }}
                next
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </PaginationItem>
          </Pagination>
        </CardBody>
      </Card>
    </>
  )
}

export default CampaignManagementTable
